import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../core/models/pagination.model';

export interface Site {
  id: string;
  name: string;
  slug: string;
  description?: string;
  is_active: boolean;
  service_charge_percent?: number;
  tenant_id?: string;
  tenant_name?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface SitesDashboard {
  total_sites: number;
  active_sites: number;
  inactive_sites: number;
}

export interface CreateSiteRequest {
  name: string;
  description?: string;
  is_active?: boolean;
  service_charge_percent?: number;
  tenant_id?: string;
  metadata?: Record<string, any>;
}

export interface UpdateSiteRequest extends Partial<CreateSiteRequest> {}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root',
})
export class SitesService {
  private readonly apiUrl = `${environment.apiUrl}/sites`;

  constructor(private http: HttpClient) {}

  /**
   * Get all sites (unpaginated) — for dropdown/cross-feature consumers.
   */
  getSites(): Observable<ApiResponse<Site[]>> {
    return this.http.get<ApiResponse<Site[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /**
   * Get a paginated page of sites for the sites list page.
   */
  getSitesPage(query: PaginationQuery = {}): Observable<Paginated<Site>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<Site[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  /**
   * Get dashboard statistics
   */
  getDashboard(): Observable<ApiResponse<SitesDashboard>> {
    return this.http.get<ApiResponse<SitesDashboard>>(
      `${this.apiUrl}/dashboard`,
    );
  }

  /**
   * Get a single site by ID
   */
  getSite(id: string): Observable<ApiResponse<Site>> {
    return this.http.get<ApiResponse<Site>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new site
   */
  createSite(data: CreateSiteRequest): Observable<ApiResponse<Site>> {
    return this.http.post<ApiResponse<Site>>(this.apiUrl, data);
  }

  /**
   * Update an existing site
   */
  updateSite(
    id: string,
    data: UpdateSiteRequest,
  ): Observable<ApiResponse<Site>> {
    return this.http.put<ApiResponse<Site>>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Delete a site
   */
  deleteSite(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Toggle site active status
   */
  toggleActive(
    id: string,
  ): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }
}
